import { Nullable } from '@enmon/commons';
import {
  MeterDetailElectricity,
  MeterDetailGas,
  MeterDetailThermal,
  MeterDetailOther,
  MeterDetailWater,
} from '@enmon/commons/lib/enmon';
import {
  MeterModelElectricityDetails,
  MeterModelGasDetails,
  MeterModelThermalDetails,
  MeterModelOtherDetails,
  MeterModelWaterDetails,
  SerializedMeterModel,
} from '@enmon/commons/lib/tools';
import { isNil } from 'rambda';
import { booleanToSelectValue } from '../../utils/booleanToSelectValue';
import type { CombinedMeterModelDetails, MeterModelDetail } from './types';

export type SuggestedValue = ReturnType<typeof getSuggestedData>['value'] | null;
export type SuggestedSelection = ReturnType<typeof getSuggestedData>['selection'] | null;

// Union of all possible meter detail keys (not types, but their keys)
type AllMeterDetailKeys =
  | keyof MeterDetailElectricity
  | keyof MeterDetailGas
  | keyof MeterDetailWater
  | keyof MeterDetailThermal
  | keyof MeterDetailOther;

// Union of all meter model detail keys to avoid intersection issues
type AllMeterModelDetailKeys =
  | keyof MeterModelElectricityDetails
  | keyof MeterModelGasDetails
  | keyof MeterModelWaterDetails
  | keyof MeterModelThermalDetails
  | keyof MeterModelOtherDetails
  | 'twin_meter';

/** Prop name conversion for backend gateway meter compatibility */
const METER_MODEL_DETAIL_TO_METER_DETAIL_MAP = {
  circuit_breaker_size: 'circuitBreakerSize',
  device_category: 'deviceCategory',
  direct: 'isDirect',
  hot_water: 'hotWater',
  i_max: 'iMax',
  i_min: 'iMin',
  i_ref: 'iRef',
  lay_length: 'layLength',
  p_max: 'pMax',
  q_max: 'qMax',
  q_min: 'qMin',
  diameter_nominal: 'diameterNominal',
  thermal_measurement_type: 'thermalMeasurementType',
  installation_position: 'installationPosition',
  measurement_category: 'measurementCategory',
  measurement_type: 'measurementType',
  g_size: 'gSize',
  tube_material: 'tubeMaterial',
} as const;

const getSuggestedData = (detail: MeterModelDetail) => {
  const isDetailArray = Array.isArray(detail);

  if (isNil(detail) || (isDetailArray && detail.length === 0)) {
    return { value: null, selection: null };
  }

  // Handle array with multiple elements - return selection only
  if (isDetailArray && detail.length > 1) {
    // Ensure selection is always a flat array, not an array of arrays
    return { value: null, selection: detail };
  }

  // Handle single value (either non-array or array with one element)
  const detailValue = isDetailArray ? detail[0] : detail;
  const value = typeof detailValue === 'boolean' ? booleanToSelectValue(detailValue) : detailValue;

  return { value, selection: null };
};

// Helper type to get the converted key or original key if no conversion exists
type GetConvertedKey<K extends AllMeterModelDetailKeys> = K extends keyof typeof METER_MODEL_DETAIL_TO_METER_DETAIL_MAP
  ? NonNullable<(typeof METER_MODEL_DETAIL_TO_METER_DETAIL_MAP)[K]>
  : K;

// Create a mapped type that converts all keys from snake_case to camelCase
// This creates BOTH original and converted keys to ensure nothing is lost
type ConvertedMeterModelDetails = {
  [K in AllMeterModelDetailKeys as GetConvertedKey<K>]?: {
    selection: SuggestedSelection;
    value: SuggestedValue;
  };
};

export type MeterModelSuggestions = ConvertedMeterModelDetails & {
  meterModelName: SerializedMeterModel['name'];
};

export const useMeterModelSuggestions = (meterModel: Nullable<SerializedMeterModel>): MeterModelSuggestions => {
  const meterModelName = meterModel?.name ?? '';
  const meterModelDetails = meterModel?.details;

  if (!meterModelDetails) return { meterModelName };

  return Object.keys(meterModelDetails).reduce<MeterModelSuggestions>(
    (acc, key) => {
      const detailKey = key as keyof CombinedMeterModelDetails;
      const convertedKey = METER_MODEL_DETAIL_TO_METER_DETAIL_MAP[detailKey] as AllMeterDetailKeys | undefined;

      const meterModelDetail = meterModelDetails[detailKey] as MeterModelDetail;

      acc[convertedKey ?? detailKey] = getSuggestedData(meterModelDetail);

      return acc;
    },
    { meterModelName },
  );
};
